/**
 * LeadRegisterPanel
 *
 * Admin → "Leads" tab. Shows the pipeline of enquiries (logged from the
 * homepage Lead Register quick-form). Filters by status + date + free
 * search. Inline status change, edit, delete, Excel export. Admins and
 * managers both have access.
 *
 * Lead Owner (the salesperson responsible) and Created By (who logged it)
 * are separate columns. The edit dialog changes every field; the owner
 * select appears only with the `leads.reassign` permission, and the
 * dialog lists the lead's reschedules and reassignments (IST).
 * `?lead=<id>` (from Team follow-ups) opens that lead's edit dialog.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Download, Search, Trash2, Pencil, Phone, RefreshCw,
  CheckCircle2, RotateCcw, Camera, X,
} from 'lucide-react';
import { api, API, useAuth } from '../../lib/store';
import { photoUrl, photoUrls, PhotoThumb, useImageViewer } from '../ImageViewer';

// Legacy status colour map kept ONLY to render badges on old records.
// New records always use the boolean `done` field; the STATUSES list
// (New/Pending/Closed/Lost) is no longer surfaced in this panel — the
// UI collapsed to Active / Done to match the LeadFormDialog change.
const LEGACY_STATUS_LABELS = {
  new: 'New', pending: 'Pending', closed: 'Closed', lost: 'Lost',
  hot: 'Hot', warm: 'Warm', cold: 'Cold', converted: 'Converted',
};
const DONE_LEGACY_STATUSES = new Set(['closed', 'lost', 'converted']);
const isLeadDone = (lead) =>
  lead?.done === true || DONE_LEGACY_STATUSES.has((lead?.status || '').toLowerCase());
const MAX_SITE_IMAGES = 5;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

// Both source lists: the homepage form's and the older admin list, so
// every stored value can be shown and kept.
const SOURCES = ['Company Exec', 'Carpenter', 'Architect', 'Site Visit', 'Referral', 'Social Media',
  'Phone', 'Instagram', 'Facebook', 'Exhibition', 'Walk-by', 'Website', 'Other'];
const ORDER_STATUSES = [
  { value: 'pending', label: 'Pending' }, { value: 'closed', label: 'Closed' }, { value: 'lost', label: 'Lost' },
];
// Fields the edit dialog sends when changed (PUT changes only what is sent).
const EDIT_FIELDS = [
  'customer_name', 'customer_phone', 'location', 'visit_date', 'source', 'interest_area',
  'architect_name', 'carpenter_name', 'budget_range', 'timeline', 'follow_up_date', 'notes',
];
const leadOwnerName = (l) => l?.owner_name
  || (l?.allocated_salesperson_id ? l?.allocated_salesperson_name : l?.salesperson_name) || '';
const leadOwnerId = (l) => l?.owner_id
  || l?.allocated_salesperson_id || l?.salesperson_id || '';
const withCurrent = (list, value) => (value && !list.includes(value) ? [value, ...list] : list);
const fmtIst = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};
const INTERESTS = ['Plywood', 'Laminates', 'Veneers', 'Highlighters', 'Hardware', 'Doors', 'ACP', 'Adhesives', 'Wooden Flooring', 'WPC/PVC', 'Multiple'];
const TIMELINES = ['This week', 'This month', '1-3 months', 'Later'];

export default function LeadRegisterPanel() {
  const { can } = useAuth();
  const canReassign = can('leads.reassign');
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkLead = searchParams.get('lead') || '';
  const [owners, setOwners] = useState([]);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ all: 0, active: 0, done: 0 });
  const [search, setSearch] = useState('');
  // Chips are now All / Closed / Pending / Lost (order_status).
  // The list is fetched without a status filter, then bucketed client-side
  // so missing/null/'' order_status folds into Pending — same rule as the
  // backend and the My Registrations tab.
  const [statusFilter, setStatusFilter] = useState(deepLinkLead ? 'all' : 'pending');
  // Visit-date range filter — used to answer "which leads actually visited in this window".
  // Logged (created_at) date filter removed per business request — the Visit range is the
  // only date lens admins actually use for leads triage.
  const [visitFrom, setVisitFrom] = useState('');
  const [visitTo, setVisitTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openViewer, viewer] = useImageViewer('Site photo');

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = { search: search || undefined };
      // No longer sending status to the server — the client
      // buckets by order_status locally so legacy done/status leads
      // continue to render without a backend migration.
      if (visitFrom) params.visit_date_from = visitFrom;
      if (visitTo) params.visit_date_to = visitTo;
      const res = await api.listLeads(params);
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
      setCounts(res.data.counts || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when filters change. We schedule via queueMicrotask so the
  // state-setter calls inside fetchLeads run *after* the effect returns —
  // required to satisfy `react-hooks/set-state-in-effect` on React 19.
  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => { if (!cancelled) fetchLeads(); });
    return () => { cancelled = true; };
    // `search`/`fetchLeads` deliberately excluded — search fetches only on
    // Enter key or the Apply button, never per keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitFrom, visitTo]);

  // Owner choices for the edit dialog (only when this user may reassign).
  useEffect(() => {
    if (!canReassign) return;
    api.getLeadOwners()
      .then((res) => setOwners(res.data?.owners || []))
      .catch(() => setOwners([]));
  }, [canReassign]);

  // Team follow-ups "Open" → ?tab=leads&lead=<id>: open that lead once loaded.
  useEffect(() => {
    if (!deepLinkLead || leads.length === 0) return;
    const hit = leads.find((x) => x.id === deepLinkLead);
    queueMicrotask(() => {
      if (hit) setEditing(hit);
      const next = new URLSearchParams(searchParams);
      next.delete('lead');
      setSearchParams(next, { replace: true });
    });
  }, [deepLinkLead, leads, searchParams, setSearchParams]);

  const handleDelete = async (lead) => {
    if (!window.confirm(`Delete lead for ${lead.customer_name}? This cannot be undone.`)) return;
    try {
      await api.deleteLead(lead.id);
      toast.success('Lead deleted');
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleToggleDone = async (lead) => {
    const nextDone = !isLeadDone(lead);
    try {
      const token = localStorage.getItem('heda_token') || '';
      await axios.patch(
        `${API}/leads/${lead.id}/done`,
        { done: nextDone },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      toast.success(nextDone ? 'Marked Done' : 'Reopened');
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleExport = async () => {
    try {
      const params = { search: search || undefined };
      params.status = statusFilter;
      if (visitFrom) params.visit_date_from = visitFrom;
      if (visitTo) params.visit_date_to = visitTo;
      const res = await api.exportLeadsXlsx(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to export Excel');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-leads-panel">
      {/* Header + summary chips */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-[17px] sm:text-[19px] font-bold text-heda-ink flex items-center gap-2">
            <Phone className="w-4 h-4 text-heda-gold" /> Lead Register
            <span className="ml-1 rounded-full bg-heda-cream px-2 py-0.5 text-[10px] font-bold tabular-nums text-heda-mute">{counts.all}</span>
          </h2>
          <p className="text-[11.5px] text-heda-mute mt-0.5">
            External enquiries before they walk in. WhatsApp reminders fire on follow-up date.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button className="h-10 text-[12.5px] rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold" variant="outline" size="sm" onClick={fetchLeads} disabled={loading} data-testid="leads-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Button className="h-10 text-[12.5px] rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold" variant="outline" size="sm" onClick={handleExport} data-testid="leads-export-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" />Excel
          </Button>
        </div>
      </div>

      {/* All / Closed / Pending / Lost dot-chip filter with
          counts. Consistent with the same widget on MyRegistrationsPage's
          Walk-ins & Leads tabs and the Staff-tab quotation chips.
          Missing/null/'' order_status buckets under Pending. */}
      <div className="flex flex-wrap gap-1.5">
        {(() => {
          const buckets = { all: leads.length, closed: 0, pending: 0, lost: 0 };
          leads.forEach((x) => {
            const s = (x?.order_status || '').toLowerCase();
            buckets[(s === 'closed' || s === 'lost') ? s : 'pending'] += 1;
          });
          return [
            { v: 'all',     label: 'All',     dot: 'bg-heda-mute' },
            { v: 'closed',  label: 'Closed',  dot: 'bg-emerald-700' },
            { v: 'pending', label: 'Pending', dot: 'bg-amber-500' },
            { v: 'lost',    label: 'Lost',    dot: 'bg-red-700' },
          ].map((chip) => (
            <button
              key={chip.v}
              onClick={() => setStatusFilter(chip.v)}
              className={
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12.5px] font-medium transition-colors ' +
                (statusFilter === chip.v ? 'bg-heda-ink border-heda-ink text-white' : 'bg-white text-heda-ink border-heda-line hover:border-heda-gold')
              }
              data-testid={`leads-filter-${chip.v}`}
            >
              <span className={`w-2 h-2 rounded-full ${chip.dot}`} />
              {chip.label} · {buckets[chip.v]}
            </button>
          ));
        })()}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-heda-mute" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchLeads(); }}
              placeholder="Name, phone, interest, notes…"
              className="rounded-xl pl-9 h-10 text-base md:text-sm bg-heda-cream/50"
              data-testid="leads-search-input"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Visit From</Label>
          <Input type="date" value={visitFrom} onChange={(e) => setVisitFrom(e.target.value)} className="rounded-xl h-10 text-base md:text-sm bg-heda-cream/50" data-testid="leads-visit-from" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Visit To</Label>
          <Input type="date" value={visitTo} onChange={(e) => setVisitTo(e.target.value)} className="rounded-xl h-10 text-base md:text-sm bg-heda-cream/50" data-testid="leads-visit-to" />
        </div>
        <Button className="h-10 rounded-xl bg-heda-ink px-4 text-white shadow-none hover:bg-black" size="sm" onClick={fetchLeads}>Apply</Button>
        {(visitFrom || visitTo) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setVisitFrom(''); setVisitTo(''); }}
            className="h-10 rounded-full text-[12px] hover:bg-heda-cream"
            data-testid="leads-clear-visit-filter"
          >
            Clear Visit
          </Button>
        )}
      </div>

      {/* Table */}
      {leads.length === 0 ? (
        <div className="heda-card flex flex-col items-center px-5 py-12 text-center">
          <span className="mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-heda-olive-tint">
            <Phone className="h-7 w-7 text-heda-olive" />
          </span>
          <p className="text-sm text-heda-mute">
            {loading ? 'Loading…' : 'No leads yet. They show up here when staff log a new enquiry from the homepage.'}
          </p>
        </div>
      ) : (
        <div className="heda-card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="heda-thead">
              <tr>
                <th className="px-3 sm:px-4 py-2.5 font-semibold">Customer</th>
                <th className="px-3 sm:px-4 py-2.5 font-semibold hidden md:table-cell">Source / Interest</th>
                <th className="px-3 sm:px-4 py-2.5 font-semibold hidden lg:table-cell">Budget / Timeline</th>
                <th className="px-3 sm:px-4 py-2.5 font-semibold hidden sm:table-cell">Follow-up</th>
                <th className="px-3 sm:px-4 py-2.5 font-semibold hidden lg:table-cell">Photos</th>
                <th className="px-3 sm:px-4 py-2.5 font-semibold">State</th>
                <th className="px-3 sm:px-4 py-2.5 font-semibold hidden md:table-cell">Owner</th>
                <th className="px-3 sm:px-4 py-2.5 font-semibold hidden lg:table-cell">Created By</th>
                <th className="px-3 sm:px-4 py-2.5 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads
                .filter((l) => {
                  if (statusFilter === 'all') return true;
                  const s = (l?.order_status || '').toLowerCase();
                  const bucket = (s === 'closed' || s === 'lost') ? s : 'pending';
                  return bucket === statusFilter;
                })
                .map((l) => {
                const done = isLeadDone(l);
                const legacyLabel = LEGACY_STATUS_LABELS[(l.status || '').toLowerCase()];
                const photos = Array.isArray(l.site_images) ? l.site_images : [];
                return (
                  <tr key={l.id} className="border-t border-heda-line hover:bg-heda-cream/50" data-testid={`lead-row-${l.id}`}>
                    <td className="px-3 sm:px-4 py-3">
                      <p className="font-semibold text-heda-ink">{l.customer_name}</p>
                      <p className="text-[11px] text-heda-mute tabular-nums">{l.customer_phone || '—'}</p>
                      {/* Owner / Created By for screens too narrow for their own columns
                          (Owner shows from md, Created By from lg). */}
                      <p className="text-[11px] text-heda-sub lg:hidden" data-testid={`lead-owner-compact-${l.id}`}>
                        <span className="md:hidden">Owner: <span className="font-medium text-heda-ink">{leadOwnerName(l) || '—'}</span> · </span>
                        Created by: {l.created_by_name || '—'}
                      </p>
                      {l.location && (
                        <p className="text-[11px] text-heda-sub" data-testid={`lead-location-${l.id}`}>📍 {l.location}</p>
                      )}
                      {(l.architect_name || l.carpenter_name) && (
                        <p className="text-[11px] text-heda-sub" data-testid={`lead-refers-${l.id}`}>
                          {l.architect_name && <span>👷 Arch: {l.architect_name}</span>}
                          {l.architect_name && l.carpenter_name && <span> · </span>}
                          {l.carpenter_name && <span>🔨 Carp: {l.carpenter_name}</span>}
                        </p>
                      )}
                      {l.visit_date && (
                        <p className="text-[11px] text-heda-mute tabular-nums" data-testid={`lead-visit-date-${l.id}`}>Visit: {l.visit_date}</p>
                      )}
                    </td>
                    <td className="px-3 sm:px-4 py-3 hidden md:table-cell">
                      <p className="text-heda-ink">{l.source || '—'}</p>
                      <p className="text-[11px] text-heda-mute">{l.interest_area || '—'}</p>
                    </td>
                    <td className="px-3 sm:px-4 py-3 hidden lg:table-cell">
                      <p className="text-heda-ink">{l.budget_range || '—'}</p>
                      <p className="text-[11px] text-heda-mute">{l.timeline || '—'}</p>
                    </td>
                    <td className="px-3 sm:px-4 py-3 tabular-nums text-heda-ink whitespace-nowrap hidden sm:table-cell">{l.follow_up_date || '—'}</td>
                    <td className="px-3 sm:px-4 py-3 hidden lg:table-cell" data-testid={`lead-photos-${l.id}`}>
                      {photos.length === 0 ? (
                        <span className="text-[11px] text-heda-mute">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1 max-w-[110px]">
                          {photos.slice(0, 4).map((name, i) => (
                            <PhotoThumb
                              key={name}
                              src={photoUrl(name, l.site_image_urls)}
                              className="w-8 h-8 rounded-lg"
                              onClick={() => openViewer(photoUrls(photos, l.site_image_urls), i)}
                              label={`Open site photo ${i + 1} of ${photos.length}`}
                              testId={`lead-photo-thumb-${l.id}-${name}`}
                            />
                          ))}
                          {photos.length > 4 && (
                            <button
                              type="button"
                              onClick={() => openViewer(photoUrls(photos, l.site_image_urls), 4)}
                              className="min-h-0 text-[11px] text-heda-mute self-center hover:text-heda-gold"
                              aria-label={`Open the other ${photos.length - 4} site photos`}
                            >
                              +{photos.length - 4}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 sm:px-4 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${done ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-sky-700'}`}
                          data-testid={`lead-state-badge-${l.id}`}
                        >
                          {done ? 'DONE' : 'ACTIVE'}
                        </span>
                        {!done && legacyLabel && legacyLabel !== 'New' && (
                          <span className="text-[11px] text-heda-mute">was: {legacyLabel}</span>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleDone(l)}
                          className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold h-8 text-[11px] px-2.5 mt-0.5"
                          data-testid={`lead-toggle-done-${l.id}`}
                        >
                          {done ? <><RotateCcw className="w-3 h-3 mr-1" />Reopen</> : <><CheckCircle2 className="w-3 h-3 mr-1" />Mark Done</>}
                        </Button>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-[12px] text-heda-ink font-medium hidden md:table-cell" data-testid={`lead-owner-${l.id}`}>{leadOwnerName(l) || '—'}</td>
                    <td className="px-3 sm:px-4 py-3 text-[12px] text-heda-sub hidden lg:table-cell" data-testid={`lead-created-by-${l.id}`}>{l.created_by_name || '—'}</td>
                    <td className="px-3 sm:px-4 py-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" className="rounded-full hover:bg-heda-cream h-9 w-9 p-0" onClick={() => setEditing(l)} data-testid={`lead-edit-${l.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="rounded-full h-9 w-9 p-0 text-red-700 hover:bg-red-50 hover:text-red-700" onClick={() => handleDelete(l)} data-testid={`lead-delete-${l.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {viewer}
      {editing && (
        <LeadEditDialog
          lead={editing}
          canReassign={canReassign}
          owners={owners}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchLeads(); }}
        />
      )}
    </div>
  );
}

function LeadEditDialog({ lead, canReassign, owners, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    ...lead,
    owner_id: leadOwnerId(lead),
    order_status: (() => {
      const s = (lead.order_status || '').toLowerCase();
      return (s === 'closed' || s === 'lost') ? s : 'pending';
    })(),
    done: isLeadDone(lead),
  }));
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  // Existing photos live on the lead doc (`site_images`). We keep a
  // local mirror so removes reflect immediately without re-fetching.
  const [existingPhotos, setExistingPhotos] = useState(
    Array.isArray(lead.site_images) ? lead.site_images : [],
  );
  // Signed links for those photos (sign-in needed), refreshed by uploads.
  const [photoLinks, setPhotoLinks] = useState(lead.site_image_urls || {});
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [pendingPreviews, setPendingPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [openViewer, viewer] = useImageViewer('Site photo');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSel = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => () => {
    pendingPreviews.forEach((u) => URL.revokeObjectURL(u));
  }, [pendingPreviews]);

  // Reschedules + reassignments (who / when, shown in IST).
  useEffect(() => {
    let cancelled = false;
    api.getLeadHistory(lead.id)
      .then((res) => { if (!cancelled) setHistory(res.data?.events || []); })
      .catch(() => { if (!cancelled) setHistory([]); });
    return () => { cancelled = true; };
  }, [lead.id]);

  // Only what changed is sent; the server leaves every other field alone.
  const buildChanges = () => {
    const out = {};
    EDIT_FIELDS.forEach((k) => {
      if ((form[k] || '') !== (lead[k] || '')) out[k] = form[k] || '';
    });
    const oldOs = (() => {
      const s = (lead.order_status || '').toLowerCase();
      return (s === 'closed' || s === 'lost') ? s : 'pending';
    })();
    if (form.order_status !== oldOs) out.order_status = form.order_status;
    if (form.order_status === 'lost' && (form.lost_reason || '') !== (lead.lost_reason || '')) {
      out.lost_reason = form.lost_reason || '';
    }
    if (form.done !== isLeadDone(lead)) out.done = form.done;
    if (canReassign && form.owner_id && form.owner_id !== leadOwnerId(lead)) out.owner_id = form.owner_id;
    return out;
  };

  const remainingSlots = MAX_SITE_IMAGES - existingPhotos.length - pendingPhotos.length;

  const handlePickPhotos = (e) => {
    const chosen = Array.from(e.target.files || []);
    if (chosen.length === 0) return;
    if (remainingSlots <= 0) {
      toast.error(`Max ${MAX_SITE_IMAGES} site photos per lead`);
      e.target.value = '';
      return;
    }
    const accepted = [];
    const previews = [];
    for (const file of chosen.slice(0, remainingSlots)) {
      if (!file.type.startsWith('image/')) { toast.error(`${file.name}: not an image`); continue; }
      if (file.size > MAX_IMAGE_BYTES) { toast.error(`${file.name}: exceeds 5 MB`); continue; }
      accepted.push(file);
      previews.push(URL.createObjectURL(file));
    }
    setPendingPhotos((p) => [...p, ...accepted]);
    setPendingPreviews((p) => [...p, ...previews]);
    e.target.value = '';
  };

  const removePendingPhoto = (idx) => {
    setPendingPhotos((prev) => prev.filter((_, i) => i !== idx));
    setPendingPreviews((prev) => {
      const removed = prev[idx];
      if (removed) URL.revokeObjectURL(removed);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const removeExistingPhoto = async (filename) => {
    if (!window.confirm('Remove this site photo?')) return;
    try {
      const token = localStorage.getItem('heda_token') || '';
      await axios.delete(`${API}/leads/${lead.id}/images/${filename}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setExistingPhotos((prev) => prev.filter((n) => n !== filename));
      toast.success('Photo removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove photo');
    }
  };

  const handleSave = async () => {
    if (!form.customer_name?.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (form.order_status === 'lost' && !(form.lost_reason || '').trim()) {
      toast.error('Enter the reason the lead was lost');
      return;
    }
    setSaving(true);
    try {
      const changes = buildChanges();
      if (Object.keys(changes).length > 0) await api.updateLead(lead.id, changes);
      if (pendingPhotos.length > 0) {
        setUploading(true);
        try {
          const fd = new FormData();
          pendingPhotos.forEach((file) => fd.append('files', file, file.name));
          const token = localStorage.getItem('heda_token') || '';
          const res = await axios.post(`${API}/leads/${lead.id}/images`, fd, {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });
          setExistingPhotos(res.data?.site_images || existingPhotos);
          setPhotoLinks(res.data?.site_image_urls || photoLinks);
        } catch (upErr) {
          toast.error(upErr.response?.data?.detail || 'Photos upload failed — other edits are saved');
        } finally {
          setUploading(false);
        }
      }
      toast.success('Lead updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="lead-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit Lead — {lead.customer_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Customer Name</Label>
            <Input className="rounded-xl bg-heda-cream/50" value={form.customer_name || ''} onChange={set('customer_name')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Lead Owner</Label>
              {canReassign ? (
                <Select value={form.owner_id || undefined} onValueChange={setSel('owner_id')}>
                  <SelectTrigger className="rounded-xl bg-heda-cream/50" data-testid="lead-edit-owner"><SelectValue placeholder={leadOwnerName(lead) || '—'} /></SelectTrigger>
                  <SelectContent>
                    {withCurrent(owners.map((o) => o.id), leadOwnerId(lead)).map((id) => {
                      const o = owners.find((x) => x.id === id);
                      return (
                        <SelectItem key={id} value={id} disabled={!o}>
                          {o ? o.name : `${leadOwnerName(lead) || id} (inactive)`}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              ) : (
                <p className="min-h-10 rounded-xl bg-heda-cream/50 px-3 py-2 text-sm text-heda-ink" data-testid="lead-edit-owner-locked">{leadOwnerName(lead) || '—'}</p>
              )}
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Created By</Label>
              <p className="min-h-10 rounded-xl bg-heda-cream/30 px-3 py-2 text-sm text-heda-sub" data-testid="lead-edit-created-by">{lead.created_by_name || '—'}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Phone</Label>
              <Input className="rounded-xl bg-heda-cream/50" value={form.customer_phone || ''} onChange={set('customer_phone')} />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Source</Label>
              <Select value={form.source || ''} onValueChange={setSel('source')}>
                <SelectTrigger className="rounded-xl bg-heda-cream/50"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{withCurrent(SOURCES, form.source).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Location</Label>
            <Input className="rounded-xl bg-heda-cream/50" value={form.location || ''} onChange={set('location')} placeholder="e.g. Hubballi, Vidyanagar" data-testid="lead-edit-location" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Visit Date</Label>
              <Input className="rounded-xl bg-heda-cream/50" type="date" value={form.visit_date || ''} onChange={set('visit_date')} data-testid="lead-edit-visit-date" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Follow-up Date</Label>
              <Input className="rounded-xl bg-heda-cream/50" type="date" value={form.follow_up_date || ''} onChange={set('follow_up_date')} data-testid="lead-edit-followup" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Architect Name</Label>
              <Input className="rounded-xl bg-heda-cream/50" value={form.architect_name || ''} onChange={set('architect_name')} placeholder="optional" data-testid="lead-edit-architect" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Carpenter Name</Label>
              <Input className="rounded-xl bg-heda-cream/50" value={form.carpenter_name || ''} onChange={set('carpenter_name')} placeholder="optional" data-testid="lead-edit-carpenter" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Interest</Label>
              <Select value={form.interest_area || ''} onValueChange={setSel('interest_area')}>
                <SelectTrigger className="rounded-xl bg-heda-cream/50"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{withCurrent(INTERESTS, form.interest_area).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Budget</Label>
              <Input className="rounded-xl bg-heda-cream/50" value={form.budget_range || ''} onChange={set('budget_range')} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Timeline</Label>
              <Select value={form.timeline || ''} onValueChange={setSel('timeline')}>
                <SelectTrigger className="rounded-xl bg-heda-cream/50"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{withCurrent(TIMELINES, form.timeline).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">State</Label>
              <Select value={form.done ? 'done' : 'active'} onValueChange={(v) => setForm((f) => ({ ...f, done: v === 'done' }))}>
                <SelectTrigger className="rounded-xl bg-heda-cream/50" data-testid="lead-edit-state"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Order Status</Label>
              <Select value={form.order_status} onValueChange={setSel('order_status')}>
                <SelectTrigger className="rounded-xl bg-heda-cream/50" data-testid="lead-edit-order-status"><SelectValue /></SelectTrigger>
                <SelectContent>{ORDER_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.order_status === 'lost' && (
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Lost Reason</Label>
                <Input className="rounded-xl bg-heda-cream/50" value={form.lost_reason || ''} onChange={set('lost_reason')} data-testid="lead-edit-lost-reason" />
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5 text-heda-gold" />
              Site Photos <span className="text-heda-mute">(up to {MAX_SITE_IMAGES})</span>
            </Label>
            <div className="mt-1 flex flex-wrap gap-2" data-testid="lead-edit-site-photos">
              {existingPhotos.map((name, i) => (
                <div
                  key={name}
                  className="relative w-16 h-16"
                  data-testid={`lead-edit-existing-photo-${name}`}
                >
                  <PhotoThumb
                    src={photoUrl(name, photoLinks)}
                    onClick={() => openViewer(photoUrls(existingPhotos, photoLinks), i)}
                    label={`Open site photo ${i + 1} of ${existingPhotos.length}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeExistingPhoto(name)}
                    className="min-h-0 absolute top-0.5 right-0.5 bg-black/60 hover:bg-black/80 text-white rounded-full w-4 h-4 flex items-center justify-center"
                    aria-label="Delete photo"
                    data-testid={`lead-edit-delete-photo-${name}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
              {pendingPreviews.map((url, idx) => (
                <div
                  key={url}
                  className="relative w-16 h-16 rounded-xl overflow-hidden border border-dashed border-heda-line bg-heda-cream"
                  data-testid={`lead-edit-pending-photo-${idx}`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover opacity-90" />
                  <button
                    type="button"
                    onClick={() => removePendingPhoto(idx)}
                    className="min-h-0 absolute top-0.5 right-0.5 bg-black/60 hover:bg-black/80 text-white rounded-full w-4 h-4 flex items-center justify-center"
                    aria-label="Remove"
                    data-testid={`lead-edit-remove-pending-${idx}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
              {remainingSlots > 0 && (
                <label
                  className="w-16 h-16 rounded-xl border border-dashed border-heda-line bg-heda-cream/50 flex flex-col items-center justify-center cursor-pointer hover:border-heda-gold"
                  data-testid="lead-edit-photo-add"
                >
                  <Camera className="w-4 h-4 text-heda-sub" />
                  <span className="text-[11px] text-heda-sub mt-0.5">Add</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handlePickPhotos}
                    data-testid="lead-edit-photo-input"
                  />
                </label>
              )}
            </div>
            <p className="text-[11px] text-heda-mute mt-1">
              {existingPhotos.length + pendingPhotos.length}/{MAX_SITE_IMAGES} · new photos upload when you click Save.
            </p>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Notes</Label>
            <Textarea className="rounded-xl bg-heda-cream/50" value={form.notes || ''} onChange={set('notes')} rows={3} />
          </div>
          <div data-testid="lead-edit-history">
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">History</Label>
            {history === null ? (
              <p className="text-[11px] text-heda-mute mt-1">Loading…</p>
            ) : history.length === 0 ? (
              <p className="text-[11px] text-heda-mute mt-1">No reschedules or reassignments yet.</p>
            ) : (
              <ul className="mt-1 space-y-1 text-[11.5px] text-heda-sub">
                {history.map((e, i) => (
                  <li key={`${e.at}-${i}`} className="rounded-lg bg-heda-cream/40 px-2 py-1" data-testid={`lead-edit-history-${i}`}>
                    <span className="tabular-nums text-heda-mute">{fmtIst(e.at)}</span>{' · '}
                    {e.type === 'reassigned' && (
                      <span>Owner <strong className="text-heda-ink">{e.from?.name || '—'}</strong> → <strong className="text-heda-ink">{e.to?.name || '—'}</strong></span>
                    )}
                    {e.type === 'rescheduled' && (
                      <span>Follow-up {e.previous_date || 'unset'} → {e.new_date || 'unset'}{e.note ? ` · "${e.note}"` : ''}</span>
                    )}
                    {e.type === 'completed' && <span>Follow-up completed</span>}
                    {!['reassigned', 'rescheduled', 'completed'].includes(e.type) && <span>{e.type}</span>}
                    {e.by_user_name && <span className="text-heda-mute"> · by {e.by_user_name}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold" variant="outline" onClick={onClose} disabled={saving || uploading}>Cancel</Button>
          <Button onClick={handleSave} className="rounded-xl bg-heda-ink text-white shadow-none hover:bg-black" disabled={saving || uploading} data-testid="lead-edit-save">
            {saving || uploading ? (uploading ? 'Uploading…' : 'Saving…') : 'Save'}
          </Button>
        </div>
        {viewer}
      </DialogContent>
    </Dialog>
  );
}
